import { MuiColorPalette } from "@masterschool/ui-calendar/dist/src/types"
import { CampusClient } from "./campusClient"

export type MsEventDto = {
  id: string
  type:
    | "live-session"
    | "regular"
    | "tutoring-session"
    | "one-on-one"
    | "availability"
  title: string
  description: string
  start: Date
  end: Date
  presentAsAllDay: boolean
  color: MuiColorPalette | undefined
  participants: string[]
  calendarId: string
  recurringGroupID?: string
  rrule?: string
  rruleUntil?: Date
  rruleTzid?: string
  hasVc: boolean
  vcUrl: string | null
  instructorCalendarId?: string
  organizerClientId: string
  recordingLinks: string[]
  // Opt-in cloud recording for non-live-session events. Live sessions are
  // always recorded by the backend regardless of this flag.
  shouldRecord?: boolean
  // Display name of the organizer, resolved server-side (shown as "Organized by").
  organizerName?: string
  // Why a requested recording wasn't made (e.g. host not a licensed Zoom user).
  recordingSkipReason?: string
  // Populated by the view-dedup (useEventActions): distinct titles of all
  // duplicate copies folded into this one. Not from the API.
  mergedTitles?: string[]
  // Populated by the view-dedup: the folded duplicate copies (id/title/
  // instructor) so the edit sidebar can reassign one copy's instructor.
  mergedEvents?: {
    id: string
    title: string
    instructorCalendarId?: string
    description?: string
  }[]
  // Populated by the view-dedup: every calendar this event is on — one per
  // folded copy (its real calendars with their colour, plus "organized" when a
  // get-organized copy folded in). Lets the calendar keep it visible + correctly
  // coloured when some of its calendars are deselected. Not from the API.
  calendarMemberships?: {
    calendarId: string
    color: MuiColorPalette | undefined
  }[]
}

type MsCalendarDtoBase = {
  id: string
  name: string
  description: string
  color: string
}

export type MsPrivateCalendarDto = MsCalendarDtoBase & {
  userClientId: string
  roles: string[]
}

export type MsOfferingCalendarDto = MsCalendarDtoBase & {
  offeringDetails: {
    offeringClientId: string
    name: string
    programId: string
  }
}

export type CourseClassCalendarDto = MsCalendarDtoBase & {
  id: string
  name: string
  description: string
  color: string
  courseClassDetails: {
    courseClassName: string
    courseClassId: string
    courseInstanceId: string
    courseId: string
    majorVersion: number | undefined
    termId: string
    language: string
  }
}

export type ProgramManagementCalendar = {
  programSchoolCalendars: MsOfferingCalendarDto[]
  programStaffCalendars: MsPrivateCalendarDto[]
  studentCalendars: MsPrivateCalendarDto[]
  allMasterschoolStaffCalendars: MsPrivateCalendarDto[]
}

// The program-calendar response also tells the client which school(s) the staff
// member owns, so the menu can pre-select those school toggles on first load.
export type ProgramManagementCalendarResponse = ProgramManagementCalendar & {
  homeDomains: string[]
}

export type CourseClassCalendarResponse = {
  courseClassCalendars: CourseClassCalendarDto[]
}

export type CustomCalendarResponse = {
  personalCalendars: MsPrivateCalendarDto[]
  schoolCalendars: MsOfferingCalendarDto[]
}

export const msEventEditableProperties = [
  "title",
  "description",
  "start",
  "end",
  "type",
  "presentAsAllDay",
  "participants",
  "rrule",
  "rruleUntil",
  "rruleTzid",
  "instructorCalendarId",
  "hasVc",
  "shouldRecord",
] as const

export type MsEventEditableProperties = Pick<
  MsEventDto,
  (typeof msEventEditableProperties)[number]
>

export type CreateMsEventDto = Omit<
  MsEventDto,
  "id" | "color" | "organizerClientId" | "vcUrl"
> & { idempotencyKey: string }

export class CalendarHubClient {
  static async create(event: CreateMsEventDto): Promise<string> {
    return CampusClient.post<string>(`ms-calendar-hub/create`, event)
  }

  // domains scopes the program calendars to those school(s). Omit it on first
  // load and the backend defaults to the staff member's home school(s), echoing
  // them back as homeDomains so the menu knows which school toggles to pre-check.
  static async getProgramCalendars(scope?: "past", domains?: string[]) {
    const params = new URLSearchParams()
    if (scope) {
      params.set("scope", scope)
    }
    if (domains && domains.length > 0) {
      params.set("domains", domains.join(","))
    }
    const query = params.toString() ? `?${params.toString()}` : ""
    return CampusClient.get<ProgramManagementCalendarResponse>(
      `ms-calendar-hub/calendars/programs${query}`,
    )
  }

  // The logged-in instructor's own calendars: their personal calendar (where
  // their sessions are attached) plus any class calendars they staff. Lets an
  // instructor see and select the things they're involved with — the LMS menu
  // is otherwise program-owner scoped and shows them nothing.
  static async getInstructorCalendars() {
    return CampusClient.get<{
      personalCalendar: MsPrivateCalendarDto
      schoolCalendars: MsOfferingCalendarDto[]
    }>("ms-calendar-hub/calendars/instructor")
  }

  // The logged-in mentor's scope, same sets the campus staff calendar shows:
  // their personal calendar, their mentees' personal calendars and the school
  // calendars those mentees belong to. One visibility source for both apps —
  // the same person must see the same calendars on campus and the LMS.
  static async getMentorCalendars() {
    return CampusClient.get<{
      personalCalendar: MsPrivateCalendarDto
      studentCalendars: MsPrivateCalendarDto[]
      schoolCalendars: MsOfferingCalendarDto[]
    }>("ms-calendar-hub/calendars/mentor")
  }

  // The logged-in career-success advisor's scope: personal calendar plus their
  // students' personal calendars (campus parity, as above).
  static async getCsaCalendars() {
    return CampusClient.get<{
      personalCalendar: MsPrivateCalendarDto
      studentCalendars: MsPrivateCalendarDto[]
    }>("ms-calendar-hub/calendars/career-success-advisor")
  }

  static async getSchoolCalendars(schoolIds: string[]) {
    return CampusClient.post<{
      schoolCalendars: MsOfferingCalendarDto[]
    }>("ms-calendar-hub/calendars/school", { schoolIds }).then(
      (response) => response.schoolCalendars,
    )
  }

  static async getCourseClassesCalendar(courseClassIds: string[]) {
    return CampusClient.post<CourseClassCalendarResponse>(
      "ms-calendar-hub/calendars/course-class",
      { courseClassIds },
    ).then((response) => response.courseClassCalendars)
  }

  static getCalendarsById(ids: string[]) {
    return CampusClient.post<CustomCalendarResponse>(
      "ms-calendar-hub/calendars",
      { calendarIds: ids },
    )
  }

  // Resolve the calendars carrying a given student's sessions (their
  // classes/programs), so toggling that student in the menu can show their
  // schedule — the student's personal calendar holds none of their classes.
  static getStudentAttendableCalendars(studentCalendarId: string) {
    return CampusClient.post<{
      personalCalendar?: MsPrivateCalendarDto
      schoolCalendars: MsOfferingCalendarDto[]
      courseClassCalendars: CourseClassCalendarDto[]
    }>("ms-calendar-hub/calendars/by-student", { studentCalendarId })
  }

  static getCalendarsByUserClientId(userClientIds: string[]) {
    return CampusClient.post<MsPrivateCalendarDto[]>(
      "ms-calendar-hub/calendars/userClientId",
      { userClientIds },
    )
  }

  static async getEvents(calendarIds: string[], from: Date, to: Date) {
    return CampusClient.post<MsEventDto[]>(`ms-calendar-hub/get-events/`, {
      calendarIds: calendarIds,
      from,
      to,
    })
  }

  static async getDrafts(from: Date, to: Date) {
    return CampusClient.get<MsEventDto[]>(
      `ms-calendar-hub/get-drafts?from=${from.toISOString()}&to=${to.toISOString()}`,
    )
  }

  // Published events the current user organized, regardless of calendar
  // selection (backs the "All organized by me" toggle).
  static async getOrganized(from: Date, to: Date) {
    return CampusClient.get<MsEventDto[]>(
      `ms-calendar-hub/get-organized?from=${from.toISOString()}&to=${to.toISOString()}`,
    )
  }

  static async edit(
    id: string,
    properties: Partial<MsEventEditableProperties>,
  ) {
    const propertiesWithNullInsteadOfUndefined = Object.fromEntries(
      Object.entries(properties).map(([key, value]) => {
        return [key, value === undefined ? null : value]
      }),
    )
    return CampusClient.post(`ms-calendar-hub/edit/`, {
      event_id: id,
      properties: propertiesWithNullInsteadOfUndefined,
    })
  }

  static async editRecurring(
    target: {
      event_id: string
      occurrence_start_time: Date
      type: "this" | "this-and-future" | "all"
    },
    properties: Partial<MsEventEditableProperties>,
  ) {
    const propertiesWithNullInsteadOfUndefined = Object.fromEntries(
      Object.entries(properties).map(([key, value]) => [
        key,
        value === undefined ? null : value,
      ]),
    )
    return CampusClient.post(`ms-calendar-hub/edit-recurrence/`, {
      target,
      properties: propertiesWithNullInsteadOfUndefined,
    })
  }

  static delete(id: string) {
    return CampusClient.delete(`ms-calendar-hub/delete`, { event_id: id })
  }

  static deleteRecurring(
    event_id: string,
    occurrence_start_time: Date,
    recurring_group_id: string,
    type: "this" | "this-and-future" | "all",
  ) {
    return CampusClient.delete(`ms-calendar-hub/delete-recurrence/`, {
      target: { event_id, occurrence_start_time, type, recurring_group_id },
    })
  }
}
